import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { useCan } from "components";
import { createFormHelper } from "lib/Form";
import { APPOINTMENT_NOTE_TYPES, NOTE_SUGGESTION_TYPES } from "models";
import { AppointmentNoteAttachments } from "modules/AppointmentDetails/components/AppointmentNoteModal/components";
import { useAppointmentNotePhoneOptions } from "modules/AppointmentDetails/components/AppointmentNotes/hooks/useAppointmentNotePhoneOptions";
import { useNoteSuggestions } from "modules/AppointmentDetails/hooks";

import type { Appointment, AppointmentNote, AppointmentNoteAttachment } from "models";

export type AppointmentNoteFormValues = Pick<
  AppointmentNote,
  "name" | "email" | "note" | "phone_nr" | "appointment_id" | "backorder_date" | "visible_to_mechanic" | "attachments"
> & {
  note_suggestion_id: number | null;
  appointment_note_id: number | null;
  phone_nr_id: string | null;
};

type AppointmentNoteFormProps = {
  hasValidationErrors: boolean;
  appointment: Appointment;
  isDeleteNonePending: boolean;
  isAddUpdateNotePending: boolean;
  noteType: APPOINTMENT_NOTE_TYPES;
  appointmentNote: AppointmentNote | null;
  onClose: () => void;
  openCustomConfirmPopup: () => void;
  onSubmit: (values: AppointmentNoteFormValues) => void;
};

const SuggestionNoteTypeMap = new Map<APPOINTMENT_NOTE_TYPES | null, NOTE_SUGGESTION_TYPES>([
  [APPOINTMENT_NOTE_TYPES.ParkingLocation, NOTE_SUGGESTION_TYPES.Parking],
  [APPOINTMENT_NOTE_TYPES.CallCustomer, NOTE_SUGGESTION_TYPES.CallCustomer]
]);

const FormHelper = createFormHelper<AppointmentNoteFormValues>();

export const AppointmentNoteForm = ({
  noteType,
  appointment,
  appointmentNote,
  isDeleteNonePending,
  hasValidationErrors,
  isAddUpdateNotePending,
  onClose,
  onSubmit,
  openCustomConfirmPopup
}: AppointmentNoteFormProps) => {
  const { t } = useTranslation();
  const { phoneOptions } = useAppointmentNotePhoneOptions(appointment);
  const suggestionOptions = useNoteSuggestions(SuggestionNoteTypeMap.get(noteType), appointment.dealer_location_id);
  const { watch, handleSubmit, setValue } = FormHelper.useFormContext();

  const [attachments, setAttachments] = useState<AppointmentNoteAttachment[] | null>(appointmentNote?.attachments || null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (appointmentNote?.attachments) setAttachments(appointmentNote.attachments);
  }, [appointmentNote?.attachments]);

  const canDeleteAppointmentNote = useCan("delete", "appointment-notes");
  const [name, email, note, phoneNr, phoneNrId] = watch(["name", "email", "note", "phone_nr", "phone_nr_id"]);

  const handleOnSuggestionChange = (noteSuggestionId?: number | null) => {
    const option = suggestionOptions.find(opt => opt.value === noteSuggestionId);
    if (option) setValue("note", option.text);
  };

  const handleOnPhoneOptionChange = (value?: string | null) => {
    if (value === "other") return;

    const option = phoneOptions.find(opt => opt.value === value);
    if (option) setValue("phone_nr", option.text);
  };

  return (
    <>
      <Modal.Content className="padding-container" scrolling>
        {suggestionOptions.length > 0 && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field>
                <FormHelper.Field.Dropdown
                  name="note_suggestion_id"
                  options={suggestionOptions}
                  dropdownProps={{
                    placeholder: t("v8_choose_answer", "Choose answer"),
                    selectOnBlur: false,
                    fluid: true
                  }}
                  onChange={value => handleOnSuggestionChange(value)}
                />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}

        {noteType === APPOINTMENT_NOTE_TYPES.TempDriver && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field required error={!name && hasValidationErrors}>
                <label>{t("v8_name", "Name")} </label>
                <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true }} />
              </Form.Field>
            </GridColumn>

            <GridColumn>
              <Form.Field required error={!phoneNr && hasValidationErrors}>
                <label>{t("v8_phone_number", "Phone Number")} </label>
                <FormHelper.Field.Input name="phone_nr" inputProps={{ placeholder: t("v8_phone_number", "Phone Number"), fluid: true }} />
              </Form.Field>
            </GridColumn>

            <GridColumn>
              <Form.Field required error={!email && hasValidationErrors}>
                <label>{t("v8_email", "Email")} </label>
                <FormHelper.Field.Input name="email" inputProps={{ placeholder: t("v8_email", "Email"), fluid: true }} />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}

        <Grid columns="equal">
          <GridColumn>
            <Form.Field
              required={![APPOINTMENT_NOTE_TYPES.Attachment, APPOINTMENT_NOTE_TYPES.Bill].includes(noteType)}
              error={hasValidationErrors && !note && ![APPOINTMENT_NOTE_TYPES.Attachment, APPOINTMENT_NOTE_TYPES.Bill].includes(noteType)}
            >
              <label>{t("v8_note", "Note")} </label>
              <FormHelper.Field.TextArea name="note" textAreaProps={{ placeholder: t("v8_note", "Note"), fluid: true, rows: 5 }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        {[APPOINTMENT_NOTE_TYPES.WO, APPOINTMENT_NOTE_TYPES.Main].includes(noteType) && (
          <Grid columns="2">
            <GridColumn floated="right">
              <div className="visible-to-mechanic-container">
                <Form.Field>
                  <FormHelper.Field.Checkbox
                    name="visible_to_mechanic"
                    label={t("v8_note_visible_to_mechanic", "Note visible to mechanic")}
                    checkboxProps={{ toggle: true }}
                  />
                </Form.Field>
              </div>
            </GridColumn>
          </Grid>
        )}

        {noteType === APPOINTMENT_NOTE_TYPES.CallCustomer && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field>
                <FormHelper.Field.Dropdown
                  name="phone_nr_id"
                  options={phoneOptions}
                  dropdownProps={{ selection: true, selectOnBlur: false, fluid: true }}
                  onChange={value => handleOnPhoneOptionChange(value)}
                />
              </Form.Field>
            </GridColumn>

            <GridColumn>
              {phoneNrId === "other" && (
                <Form.Field error={hasValidationErrors && !phoneNr} required>
                  <FormHelper.Field.Input name="phone_nr" inputProps={{ placeholder: t("v8_enter_phone_number", "Enter phone number"), fluid: true }} />
                </Form.Field>
              )}
            </GridColumn>
          </Grid>
        )}

        {noteType === APPOINTMENT_NOTE_TYPES.BackOrder && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field>
                <FormHelper.Field.DatePicker name="backorder_date" />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}

        {noteType !== APPOINTMENT_NOTE_TYPES.NoDrive && (
          <AppointmentNoteAttachments
            noteType={noteType}
            attachments={attachments}
            appointment={appointment}
            appointmentNote={appointmentNote}
            hasValidationErrors={hasValidationErrors}
            setAttachments={setAttachments}
            onUpload={setIsUploading}
          />
        )}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="action-buttons">
          <div>
            {Boolean(appointmentNote) && canDeleteAppointmentNote && (
              <Form.Button type="button" onClick={openCustomConfirmPopup} color="red" loading={isDeleteNonePending} disabled={isAddUpdateNotePending}>
                {t("v8_delete", "Delete")}
                <Icon className="trash" />
              </Form.Button>
            )}
          </div>

          <div className="save-cancel-buttons">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>

            <Form.Button
              type="submit"
              color="green"
              disabled={isDeleteNonePending || isUploading}
              loading={isAddUpdateNotePending}
              onClick={handleSubmit(d => onSubmit({ ...d, ...(attachments && { attachments }) }))}
            >
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
